import { useRef, useState } from 'react';
import axios from 'axios';
import { Download, Upload, Loader2, FileJson } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { API_URL } from '../../config';

function slugifyName(name) {
  return (name || 'space')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'space';
}

/**
 * Space-level Download / Import documents as DocTree JSON
 * (format: doctree-space-export).
 */
export default function SpaceDocumentsTransfer({
  spaceId,
  spaceName,
  canEdit = false,
  onImported,
}) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { payload, fileName }

  const downloadJson = async () => {
    if (!spaceId) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API_URL}/spaces/${spaceId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slugifyName(spaceName || data?.space?.name)}-documents.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        `Downloaded ${(data.documents || []).length} document${
          (data.documents || []).length === 1 ? '' : 's'
        }`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export documents');
    } finally {
      setBusy(false);
    }
  };

  const onFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const docs = Array.isArray(parsed)
        ? parsed
        : parsed.documents || parsed.documents_snapshot;
      if (!Array.isArray(docs) || docs.length === 0) {
        toast.error('This JSON has no documents to import');
        return;
      }
      setPendingImport({ payload: parsed, fileName: file.name, count: docs.length });
    } catch {
      toast.error('Could not read that file as JSON');
    }
  };

  const runImport = async (mode) => {
    if (!pendingImport || !spaceId) return;
    setBusy(true);
    try {
      const body =
        pendingImport.payload && typeof pendingImport.payload === 'object'
          ? { ...pendingImport.payload, mode }
          : { mode, documents: pendingImport.payload };

      const { data } = await axios.post(`${API_URL}/spaces/${spaceId}/import`, body);
      toast.success(
        mode === 'replace'
          ? `Replaced space with ${data.imported} document(s)`
          : `Imported ${data.imported} document(s)`
      );
      setPendingImport(null);
      if (onImported) await onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={onFileChosen}
        data-testid="space-import-file-input"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            className="gap-1.5"
            data-testid="space-documents-transfer-menu"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileJson className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">JSON</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            onClick={downloadJson}
            disabled={busy}
            data-testid="space-export-json"
          >
            <Download className="w-4 h-4 mr-2" />
            Download as JSON
          </DropdownMenuItem>
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                data-testid="space-import-json"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import JSON…
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={Boolean(pendingImport)}
        onOpenChange={(open) => {
          if (!open && !busy) setPendingImport(null);
        }}
      >
        <AlertDialogContent data-testid="space-import-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Import documents</AlertDialogTitle>
            <AlertDialogDescription>
              File “{pendingImport?.fileName}” contains {pendingImport?.count} document
              {pendingImport?.count === 1 ? '' : 's'} (including nested pages). Merge adds them
              alongside existing pages. Replace deletes all current documents in this space first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => runImport('merge')}
              data-testid="space-import-merge"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Merge
            </Button>
            <AlertDialogAction
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                runImport('replace');
              }}
              data-testid="space-import-replace"
            >
              Replace all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
